import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
  Textarea,
} from "../../shared/ui";
import type {
  CarryoverView,
  PageResponse,
  SprintDetail,
  SprintSummary,
  TeamSummary,
} from "../../shared/types/api";

/**
 * Creates a sprint (§48).
 *
 * <p>Dates default to a fortnight starting next Monday, because that is what most sprints are and
 * a form that opens empty makes somebody count days in their head. Both are editable.
 *
 * <p>The goal is asked for prominently rather than tucked away as an optional note. A sprint whose
 * goal is "finish the tickets" has no goal, and its review has nothing to be held against — §50
 * puts the goal at the top of the active-sprint view for the same reason.
 */
export function CreateSprintDrawer({
  projectId,
  open,
  onClose,
  onSaved,
}: {
  projectId: string;
  open: boolean;
  onClose: () => void;
  onSaved: (sprintId?: string) => void;
}) {
  const [name, setName] = useState("");
  const [goal, setGoal] = useState("");
  const [teamId, setTeamId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [carryOver, setCarryOver] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /**
   * What earlier sprints did not finish, so the offer names it rather than being abstract.
   *
   * <p>"Bring in 3 items left over from Sprint 1" is a decision somebody can make; "carry over
   * unfinished work" is a checkbox nobody reads.
   */
  const waiting = useQuery({
    queryKey: ["backlog-carryover", projectId],
    queryFn: () =>
      api.get<CarryoverView[]>(`/api/v1/backlog/carryover?projectId=${projectId}`),
    enabled: open && Boolean(projectId),
  });

  const teams = useQuery({
    queryKey: ["teams", "picker"],
    queryFn: () =>
      api.get<PageResponse<TeamSummary>>("/api/v1/teams?size=200&sort=name,asc"),
    enabled: open,
  });

  /** Existing sprints, only to suggest the next number in the name. */
  const existing = useQuery({
    queryKey: ["sprints", projectId],
    queryFn: () => api.get<SprintSummary[]>(`/api/v1/sprints?projectId=${projectId}`),
    enabled: open && Boolean(projectId),
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    const start = nextMonday();
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 11); // Monday to the Friday of the following week.

    const next = (existing.data?.length ?? 0) + 1;
    setName(`Sprint ${next}`);
    setGoal("");
    setTeamId("");
    setStartDate(iso(start));
    setEndDate(iso(end));
    setCarryOver(true);
    setError(null);
  }, [open, existing.data]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await api.post<SprintDetail>("/api/v1/sprints", {
        projectId,
        name,
        goal: goal || null,
        teamId: teamId || null,
        startDate,
        endDate,
        carryOverWaitingItems: carryOver,
      });
      onSaved(created.sprint.id);
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not create the sprint.");
    } finally {
      setSaving(false);
    }
  };

  const backwards = Boolean(startDate && endDate) && endDate < startDate;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="New sprint"
      subtitle="A time box, a goal, and the work the team commits to."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={name.trim() === "" || backwards}
          >
            {saving ? "Creating…" : "Create sprint"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field label="Name" required>
          <Input value={name} onChange={(event) => setName(event.target.value)} />
        </Field>

        <Field
          label="Sprint goal"
          hint="One sentence about what this sprint is for. The review is held against it."
        >
          <Textarea
            rows={2}
            value={goal}
            onChange={(event) => setGoal(event.target.value)}
            placeholder="Customers can pay by card end to end, including refunds."
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Starts" required>
            <Input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </Field>
          <Field label="Ends" required>
            <Input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </Field>
        </div>

        {backwards && (
          <InlineAlert tone="warning">A sprint cannot end before it starts.</InlineAlert>
        )}

        <Field
          label="Team"
          hint="Optional. Setting it lets a project run one sprint per team, and scopes the capacity calculation to that team."
        >
          <Select value={teamId} onChange={(event) => setTeamId(event.target.value)}>
            <option value="">Everyone on the project</option>
            {(teams.data?.content ?? []).map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </Select>
        </Field>

        {/*
          * Offered only when there is something to offer, and named rather than described. A
          * standing checkbox that reads "carry over unfinished work" on a project with nothing
          * waiting is noise; this appears exactly when it is a decision.
          */}
        {(waiting.data?.length ?? 0) > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={carryOver}
                onChange={(event) => setCarryOver(event.target.checked)}
              />
              <span>
                <span className="text-sm font-medium text-ink-900">
                  Bring in {waiting.data?.length} unfinished{" "}
                  {waiting.data?.length === 1 ? "item" : "items"} from{" "}
                  {[...new Set((waiting.data ?? []).map((row) => row.fromSprintName))].join(", ")}
                </span>
                <span className="mt-0.5 block text-xs text-ink-600">
                  {(waiting.data ?? [])
                    .slice(0, 3)
                    .map((row) => `${row.itemKey} (${row.reasonLabel})`)
                    .join(", ")}
                  {(waiting.data?.length ?? 0) > 3 &&
                    `, and ${(waiting.data?.length ?? 0) - 3} more`}
                  . Anything you do not want can be moved back out before the sprint starts.
                </span>
              </span>
            </label>
          </div>
        )}

        <InlineAlert tone="info">
          Creating a sprint does not commit to anything. Pull work in from the backlog first —
          starting the sprint is what records what was committed, and that record cannot be
          rewritten afterwards.
        </InlineAlert>
      </div>
    </Drawer>
  );
}

/** The next Monday, in UTC so the date does not shift by timezone. */
function nextMonday(): Date {
  const today = new Date();
  const date = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  const daysUntilMonday = (8 - date.getUTCDay()) % 7 || 7;
  date.setUTCDate(date.getUTCDate() + daysUntilMonday);
  return date;
}

function iso(date: Date): string {
  return date.toISOString().slice(0, 10);
}
